import { useEffect } from 'react';
import { useTranslate } from '../../application/i18n/LocaleProvider';
import { useJukebox } from '../../application/sound/JukeboxProvider';
import { formatTrackTime } from '../../domain/sound/soundtrack';
import { EqualiserIcon } from '../icons/JukeboxIcons';
import JukeboxDeck from '../sound/JukeboxDeck';
import trackSubtitle from '../sound/trackVersion';

/**
 * The soundtrack, as a place you go (SPEC-014 PR-14.3).
 *
 * A SCREEN RATHER THAN A SHEET, and it is worth saying why since this app's
 * other two panels of controls — the garage's filters and the account — are
 * both `<dialog>`s. Those act on the screen behind them: a filter narrows the
 * shelf you can see, and dismissing it is finishing the job. A jukebox does not
 * act on anything. It is a shelf of its own with its own artwork, its own
 * running order and twenty-four minutes of content, and a bottom sheet over
 * somebody else's screen is the wrong container for that — you cannot arrive at
 * it, link to it, or come back to where you were in it.
 *
 * NOTHING HERE MAKES THE SOUND. The element is in `JukeboxProvider`, above the
 * route table, which is what makes the third tab a place you can leave: pressing
 * play here and walking to the garage does not stop the music, and coming back
 * finds it where it got to.
 *
 * The shelf is fetched when this screen first opens rather than at boot, so a
 * reader who never presses the tab never pays for the request. `loadShelf` is
 * guarded against a second call, so coming back costs nothing.
 */
export default function JukeboxScreen() {
    const t = useTranslate();
    const jukebox = useJukebox();
    const loadShelf = jukebox?.loadShelf;

    useEffect(() => {
        if (loadShelf) {
            loadShelf();
        }
    }, [loadShelf]);

    if (!jukebox) {
        return null;
    }

    const { tracks, track, status, isPlaying, selectTrack } = jukebox;
    /* Seventeen recordings of eleven titles, so the count of ROWS undersells what
       is on the shelf and the count of titles undersells it the other way. The
       foot says both — and says only the rows on a shelf where every track is
       its own title, which is what it was until the versions landed. */
    const titles = new Set(tracks.map((row) => row.family)).size;

    return (
        <section className="jukebox-screen">
            <header className="jukebox-screen__head">
                <h1 className="jukebox-screen__title">{t('jukebox.title')}</h1>
                <p className="jukebox-screen__intro">{t('jukebox.intro')}</p>
            </header>

            {status === 'ready' && track ? <JukeboxDeck jukebox={jukebox} /> : null}

            {/* Announced when it changes: a reader who opened this tab on a bad
                connection has nothing else on screen that moved. */}
            <p className="jukebox-screen__status" role="status">
                {status === 'loading' ? t('jukebox.loading') : null}
                {status === 'failed' ? t('jukebox.failed') : null}
            </p>

            {status === 'failed' ? (
                <button type="button" className="button button--quiet" onClick={loadShelf}>
                    {t('jukebox.retry')}
                </button>
            ) : null}

            {tracks.length > 0 ? (
                <>
                    <ul className="jukebox__shelf">
                        {tracks.map((row, position) => {
                            const isCurrent = row.id === track?.id;
                            const subtitle = trackSubtitle(row, t);

                            return (
                                <li key={row.id}>
                                    <button
                                        type="button"
                                        className={`jukebox__row${isCurrent ? ' jukebox__row--current' : ''}`}
                                        onClick={() => selectTrack(position)}
                                        /* The playing row is the current item in
                                           this list, which is what a screen
                                           reader is given instead of the moving
                                           bars beside it. */
                                        aria-current={isCurrent ? 'true' : undefined}
                                        /* The accessible name carries the
                                           subtitle too: three rows reading "Play
                                           4.1 Seconds" would be three
                                           indistinguishable buttons to anybody
                                           listening rather than looking. The
                                           bare form is the fallback for a shelf
                                           whose manifest carries no styles. */
                                        aria-label={subtitle
                                            ? t('jukebox.playDescribed', { title: row.title, description: subtitle })
                                            : t('jukebox.playTrack', { title: row.title })}
                                    >
                                        {row.cover ? (
                                            <img
                                                className="jukebox__thumb"
                                                src={row.cover.src}
                                                srcSet={row.cover.srcSet}
                                                /* The wall's column count,
                                                   restated for a parser that
                                                   runs BEFORE the stylesheet.
                                                   ------------------------------
                                                   `sizes` is read at preload
                                                   time, so a thumbnail promoted
                                                   to a 250px tile while this
                                                   still said 52px would fetch
                                                   the 240 rung and upscale it —
                                                   a desktop that looks worse
                                                   than the phone, with no error
                                                   anywhere.

                                                   Each step is the widest tile
                                                   its band reaches, mirroring
                                                   `.jukebox__shelf` in
                                                   `jukeboxShelf.css` against the
                                                   pane widths in `jukebox.css`:
                                                   three columns of the whole
                                                   stage from 1024, three beside
                                                   the deck from 1200, four from
                                                   1440, five from 1920. Stating
                                                   the top of each band means the
                                                   bottom of it over-fetches
                                                   slightly, which is the safe
                                                   direction — the other one is
                                                   invisible. Change either sheet
                                                   and this line is part of the
                                                   change. */
                                                sizes="(min-width: 1920px) 220px, (min-width: 1440px) 225px, (min-width: 1200px) 250px, (min-width: 1024px) 301px, 52px"
                                                loading="lazy"
                                                alt=""
                                            />
                                        ) : (
                                            <span className="jukebox__thumb jukebox__thumb--empty" />
                                        )}

                                        <span className="jukebox__row-name">
                                            <span className="jukebox__row-title">{row.title}</span>
                                            {subtitle ? (
                                                <span className="jukebox__row-variant">{subtitle}</span>
                                            ) : null}
                                        </span>

                                        {isCurrent && isPlaying ? (
                                            <EqualiserIcon className="jukebox__row-meter" size={16} />
                                        ) : null}

                                        <span className="jukebox__row-time">{formatTrackTime(row.seconds)}</span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>

                    {/* Worth saying on a phone: this shelf is thirty-eight
                        megabytes and none of it is fetched until a track is
                        chosen. */}
                    <p className="jukebox__foot">
                        {titles < tracks.length
                            ? t('jukebox.countTitles', { count: tracks.length, titles })
                            : t('jukebox.count', { count: tracks.length })}
                        {' · '}
                        {t('jukebox.streaming')}
                    </p>
                </>
            ) : null}
        </section>
    );
}
